import type { TripLegAddress } from '@/data/models/reservation.model';

import { isRoundTripReturnLeg } from './tripLegConfidence';

/**
 * Saved passenger picks often land on the address field machines without a
 * Google `place_id` (AddressInput skips Place Details for them, so
 * `*AddressStruct` stays empty). Trip-leg validation then has nothing to
 * score. These helpers look the place up from the selected address's display
 * text and stamp the structured address + place id onto the reservation
 * payload (and, via `resolvedFormAddresses`, back onto the form).
 */

/** Same exclusions AddressInput uses so we don't resolve a city/neighborhood. */
const excludedPlaceTypes = [
  'locality',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'neighborhood',
  'continent',
  'country',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'administrative_area_level_6',
  'administrative_area_level_7',
];

export type ResolvedPlace = {
  place_id: string;
  address: TripLegAddress;
  zipCode?: string;
};

export type ResolvePlaceFn = (query: string) => Promise<ResolvedPlace | null>;

export type ResolvedFormAddress = {
  tripIndex: number;
  field: 'pickupAddress' | 'dropoffAddress';
  /** Enriched Places-shaped value to write back onto `trips.N.{field}`. */
  place: Record<string, unknown>;
  addressStruct: string;
};

type SelectedPlace = {
  description?: string;
  mainText?: string;
  secondaryText?: string;
  zipCode?: string;
  place_id?: string;
  isHeader?: boolean;
};

const placeResolveCache = new Map<string, Promise<ResolvedPlace | null>>();

export function clearPlaceResolveCache(): void {
  placeResolveCache.clear();
}

function asSelectedPlace(value: unknown): SelectedPlace | null {
  if (value == null) return null;
  if (typeof value === 'object') return value as SelectedPlace;
  return null;
}

/**
 * True when the field-machine / form selection cannot be handed to Place
 * Details as-is: missing id, header row, or the legacy `passenger-` prefix
 * AddressInput still treats as "don't fetch".
 */
export function selectionNeedsPlaceId(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  const place = asSelectedPlace(value);
  if (!place || place.isHeader) return false;
  const id = place.place_id?.trim() ?? '';
  if (!id || id === 'header' || id.startsWith('passenger-')) return true;
  return false;
}

/** Autocomplete query from an AddressInput PlaceType (or a typed string). */
export function addressQueryFromSelection(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  const place = asSelectedPlace(value);
  if (!place || place.isHeader) return '';
  if (place.mainText) {
    const street = place.mainText;
    let city = place.secondaryText || '';
    const zip = place.zipCode;
    if (zip) city = city.replace(` ${zip}`, '');
    return `${street} ${city} ${zip || ''}`.trim();
  }
  return `${place.description ?? ''} ${place.zipCode ?? ''}`.trim();
}

function getAddrComponent(
  addrComponents: ReadonlyArray<{
    types?: readonly string[];
    shortText?: string | null;
    longText?: string | null;
  }>,
  name: string,
  short: boolean = false,
): string {
  const comp = addrComponents.find((c) =>
    (c.types as readonly string[] | undefined)?.includes(name),
  );
  return comp ? (short ? (comp.shortText ?? '') : (comp.longText ?? '')) : '';
}

export function convertAddressComponents(
  addrComponents: ReadonlyArray<{
    types?: readonly string[];
    shortText?: string | null;
    longText?: string | null;
  }>,
): TripLegAddress {
  const streetNumber = getAddrComponent(addrComponents, 'street_number');
  const sub = getAddrComponent(addrComponents, 'subpremise') || undefined;
  return {
    addressLine1:
      `${streetNumber} ${getAddrComponent(addrComponents, 'route')}`.trim(),
    addressLine2: sub,
    city:
      getAddrComponent(addrComponents, 'locality') ||
      getAddrComponent(addrComponents, 'political'),
    stateCode: getAddrComponent(
      addrComponents,
      'administrative_area_level_1',
      true,
    ),
    county: getAddrComponent(
      addrComponents,
      'administrative_area_level_2',
      true,
    ),
    zipCode: getAddrComponent(addrComponents, 'postal_code'),
  };
}

function placeFromSelection(
  selection: unknown,
  resolved: ResolvedPlace,
): Record<string, unknown> {
  const base =
    selection != null && typeof selection === 'object'
      ? { ...(selection as Record<string, unknown>) }
      : {};
  const { address } = resolved;
  return {
    ...base,
    description:
      (typeof base.description === 'string' && base.description) ||
      [address.addressLine1, address.city, address.stateCode, address.zipCode]
        .filter(Boolean)
        .join(', '),
    mainText: address.addressLine1,
    secondaryText:
      `${address.city}, ${address.stateCode} ${address.zipCode}`.trim(),
    mainTextMatchedSubstrings: Array.isArray(base.mainTextMatchedSubstrings)
      ? base.mainTextMatchedSubstrings
      : [],
    place_id: resolved.place_id,
    zipCode: resolved.zipCode ?? address.zipCode,
    isHeader: false,
  };
}

/**
 * Google Places Autocomplete + Place Details, matching AddressInput's new
 * Places API. `places` is the Maps `PlacesLibrary` (may be undefined while
 * the loader is still importing). Failures resolve to `null` so validation
 * can continue with the original (empty) address.
 */
export async function resolvePlaceFromGooglePlaces(
  query: string,
  places: google.maps.PlacesLibrary | undefined,
): Promise<ResolvedPlace | null> {
  const trimmed = query.trim();
  if (!trimmed || !places) return null;

  const cached = placeResolveCache.get(trimmed);
  if (cached) return cached;

  const promise = (async (): Promise<ResolvedPlace | null> => {
    try {
      const sessionToken = new places.AutocompleteSessionToken();
      const { suggestions } = await (
        places as google.maps.PlacesLibrary & {
          AutocompleteSuggestion: {
            fetchAutocompleteSuggestions: (req: {
              input: string;
              sessionToken: unknown;
            }) => Promise<{ suggestions?: unknown[] }>;
          };
        }
      ).AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: trimmed,
        sessionToken,
      });
      const filtered = (suggestions ?? []).filter((suggestion) => {
        const types = (
          suggestion as {
            placePrediction?: { types?: string[] };
          }
        ).placePrediction?.types;
        if (!Array.isArray(types)) return true;
        return !excludedPlaceTypes.some((type) => types.includes(type));
      });
      const placeId = (
        filtered[0] as { placePrediction?: { placeId?: string } } | undefined
      )?.placePrediction?.placeId;
      if (!placeId) return null;

      const place = new places.Place({
        id: placeId,
        requestedLanguage: 'en',
      });
      await place.fetchFields({
        fields: ['addressComponents', 'location', 'formattedAddress'],
      });
      const address = convertAddressComponents(place.addressComponents ?? []);
      if (!address.addressLine1?.trim()) return null;
      const location = place.location;
      if (location) {
        address.latitude = location.lat();
        address.longitude = location.lng();
      }
      address.placeID = placeId;
      return { place_id: placeId, address, zipCode: address.zipCode };
    } catch {
      // Transient network / quota / API errors should not poison the cache:
      // the next validation for this query needs a fresh Places call.
      placeResolveCache.delete(trimmed);
      return null;
    }
  })();

  placeResolveCache.set(trimmed, promise);
  // Autocomplete returning zero suggestions is deterministic for a given
  // query, so keeping that `null` avoids re-hitting Google on every
  // validation. Thrown errors drop the cache entry above so they can retry.
  return promise;
}

async function resolveSelection(
  selection: unknown,
  resolvePlace: ResolvePlaceFn,
): Promise<ResolvedPlace | null> {
  if (!selectionNeedsPlaceId(selection)) return null;
  const query = addressQueryFromSelection(selection);
  if (!query) return null;
  return resolvePlace(query);
}

function formIndexForLeg(leg: unknown, legIndex: number): number {
  if (isRoundTripReturnLeg(leg)) return 0;
  const sequence = (leg as { tripLegSequence?: number } | null | undefined)
    ?.tripLegSequence;
  if (
    typeof sequence === 'number' &&
    Number.isFinite(sequence) &&
    sequence > 0
  ) {
    return sequence - 1;
  }
  return legIndex;
}

export async function enrichReservationMissingPlaceIds(input: {
  reservation: unknown;
  selectedPickups: unknown[];
  selectedDropoffs: unknown[];
  resolvePlace: ResolvePlaceFn;
}): Promise<{
  reservation: unknown;
  resolvedFormAddresses: ResolvedFormAddress[];
}> {
  const { reservation, selectedPickups, selectedDropoffs, resolvePlace } =
    input;
  const tripLegs = (
    reservation as { tripLegs?: Array<Record<string, unknown>> } | null
  )?.tripLegs;
  if (!Array.isArray(tripLegs) || tripLegs.length === 0) {
    return { reservation, resolvedFormAddresses: [] };
  }

  const pickupByFormIndex = new Map<number, ResolvedPlace>();
  const dropoffByFormIndex = new Map<number, ResolvedPlace>();
  const resolvedFormAddresses: ResolvedFormAddress[] = [];

  const formIndexes = new Set<number>();
  for (let i = 0; i < tripLegs.length; i++) {
    formIndexes.add(formIndexForLeg(tripLegs[i], i));
  }

  await Promise.all(
    Array.from(formIndexes).map(async (formIndex) => {
      const selectedPickup = selectedPickups[formIndex];
      const selectedDropoff = selectedDropoffs[formIndex];
      const [pickupResolved, dropoffResolved] = await Promise.all([
        resolveSelection(selectedPickup, resolvePlace),
        resolveSelection(selectedDropoff, resolvePlace),
      ]);
      if (pickupResolved) {
        pickupByFormIndex.set(formIndex, pickupResolved);
        resolvedFormAddresses.push({
          tripIndex: formIndex,
          field: 'pickupAddress',
          place: placeFromSelection(selectedPickup, pickupResolved),
          addressStruct: JSON.stringify(pickupResolved.address),
        });
      }
      if (dropoffResolved) {
        dropoffByFormIndex.set(formIndex, dropoffResolved);
        resolvedFormAddresses.push({
          tripIndex: formIndex,
          field: 'dropoffAddress',
          place: placeFromSelection(selectedDropoff, dropoffResolved),
          addressStruct: JSON.stringify(dropoffResolved.address),
        });
      }
    }),
  );

  if (pickupByFormIndex.size === 0 && dropoffByFormIndex.size === 0) {
    return { reservation, resolvedFormAddresses: [] };
  }

  const nextLegs = tripLegs.map((leg, index) => {
    const formIndex = formIndexForLeg(leg, index);
    const isReturn = isRoundTripReturnLeg(leg);
    const pickupResolved = isReturn
      ? dropoffByFormIndex.get(formIndex)
      : pickupByFormIndex.get(formIndex);
    const dropoffResolved = isReturn
      ? pickupByFormIndex.get(formIndex)
      : dropoffByFormIndex.get(formIndex);
    if (!pickupResolved && !dropoffResolved) return leg;
    return {
      ...leg,
      ...(pickupResolved ? { pickUpAddress: pickupResolved.address } : {}),
      ...(dropoffResolved ? { dropOffAddress: dropoffResolved.address } : {}),
    };
  });

  return {
    reservation: {
      ...(reservation as Record<string, unknown>),
      tripLegs: nextLegs,
    },
    resolvedFormAddresses,
  };
}
